<script setup lang="ts">
    import { computed, inject, onMounted, ref, watch } from 'vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import EventEditorDetailsNav from '@views/Event/EventEditor/Details/EventEditorDetailsNav.vue';
    import { fetchParticipantStatus } from '@views/Event/Event.access';
    import { type FullParticipantStatus } from '@views/Event/EventEditor/GuestList/guest-list-types';
    import { eventKey, saveEventPropertyKey, canEditKey } from '@views/Event/EventEditor/event-editor-keys';

    const props = defineProps<{
        eventId: string;
    }>();

    const event = inject(eventKey)!;
    const canEdit = inject(canEditKey)!;
    const saveEventProperty = inject(saveEventPropertyKey)!;

    const registrationLimit = ref(0);
    const registrationQueueLimit = ref(0);

    watch(
        () => event.value?.RegistrationLimit,
        (value) => (registrationLimit.value = Number(value ?? 0)),
        { immediate: true }
    );
    watch(
        () => event.value?.RegistrationQueueLimit,
        (value) => (registrationQueueLimit.value = Number(value ?? 0)),
        { immediate: true }
    );

    function saveRegistrationLimit(newValue: number) {
        registrationLimit.value = newValue;
        saveEventProperty('RegistrationLimit', String(newValue), $Resources.EventLimitLabel);
        // Without a limit the queue limit is hidden, so clear it whether the limit was toggled off or emptied
        if (newValue === 0 && registrationQueueLimit.value !== 0) saveRegistrationQueueLimit(0);
    }

    function saveRegistrationQueueLimit(newValue: number) {
        registrationQueueLimit.value = newValue;
        saveEventProperty('RegistrationQueueLimit', String(newValue), $Resources.EventQueueLimitLabel);
    }

    // Same statuses the public site counts when deciding whether the event is full
    const participantStatus = ref<FullParticipantStatus | null>(null);
    const bookedCount = computed(() => (participantStatus.value ? participantStatus.value.Accepted + participantStatus.value.Attended : 0));
    const queuedCount = computed(() => participantStatus.value?.Waitlist ?? 0);
    const spotsLeft = computed(() => Math.max(registrationLimit.value - bookedCount.value, 0));
    const queueSpotsLeft = computed(() => Math.max(registrationQueueLimit.value - queuedCount.value, 0));

    onMounted(async () => {
        try {
            participantStatus.value = await fetchParticipantStatus(props.eventId);
        } catch {
            // Counts are informational only; the limits can still be edited without them
        }
    });

    function formatCount(template: string, count: number) {
        return (template ?? '').replace('{Count}', String(count));
    }

    // Bound with v-model: FormInput re-renders on the first keystroke, which would otherwise reset the input to a stale modelValue
    const registrationLimitDraft = ref<string | number>(0);
    const registrationQueueLimitDraft = ref<string | number>(0);
    watch(registrationLimit, (value) => (registrationLimitDraft.value = value), { immediate: true });
    watch(registrationQueueLimit, (value) => (registrationQueueLimitDraft.value = value), { immediate: true });

    // Toggling is how a limit is removed, so an empty, zero or invalid value restores the saved one
    function parseLimit(value: string | number): number | null {
        const newValue = Math.floor(Number(value));
        return value !== '' && Number.isFinite(newValue) && newValue >= 1 ? newValue : null;
    }

    function onRegistrationLimitBlur() {
        const newValue = parseLimit(registrationLimitDraft.value);
        registrationLimitDraft.value = newValue ?? registrationLimit.value;
        if (newValue !== null && newValue !== registrationLimit.value) saveRegistrationLimit(newValue);
    }

    function onRegistrationQueueLimitBlur() {
        const newValue = parseLimit(registrationQueueLimitDraft.value);
        registrationQueueLimitDraft.value = newValue ?? registrationQueueLimit.value;
        if (newValue !== null && newValue !== registrationQueueLimit.value) saveRegistrationQueueLimit(newValue);
    }

    function toggleRegistrationLimit() {
        saveRegistrationLimit(registrationLimit.value === 0 ? 100 : 0);
    }

    function toggleRegistrationQueueLimit() {
        saveRegistrationQueueLimit(registrationQueueLimit.value === 0 ? 20 : 0);
    }
</script>

<template>
    <div v-if="event" class="max-700">
        <h2 class="h3 m-0-t m-lg-b">{{ $Resources.EventHowManyCanAttendLabel }}</h2>

        <div class="form-group m-sm-b">
            <FormCheckable
                :label="$Resources.EventSetMaxLimitAttendeeToggleLabel"
                :switch-style="SwitchStyle.Standard"
                :checked="registrationLimit !== 0"
                :disabled="!canEdit"
                @update:modelValue="toggleRegistrationLimit" />
        </div>

        <div v-if="registrationLimit !== 0" class="form-group d-flex flex-wrap align-items-end gap-lg">
            <FormInput
                type="number"
                :label="$Resources.EventLimitLabel"
                v-model="registrationLimitDraft"
                :disabled="!canEdit"
                :min="1"
                :widthInChars="6"
                @onBlur="onRegistrationLimitBlur">
                <template #aside>
                    <p v-if="participantStatus && spotsLeft !== registrationLimit" class="align-with-input m-0-b p-xs-l">
                        {{ formatCount($Resources.EventLimitSpotsLeftText, spotsLeft) }}
                    </p>
                </template>
            </FormInput>
        </div>

        <template v-if="registrationLimit !== 0">
            <div class="form-group m-xl-t m-sm-b">
                <FormCheckable
                    :label="$Resources.EventSetAttendeeQueueLimitToggleLabel"
                    :switch-style="SwitchStyle.Standard"
                    :checked="registrationQueueLimit !== 0"
                    :disabled="!canEdit"
                    @update:modelValue="toggleRegistrationQueueLimit" />
            </div>

            <div v-if="registrationQueueLimit !== 0" class="form-group d-flex flex-wrap align-items-end gap-lg">
                <FormInput
                    type="number"
                    :label="$Resources.EventQueueLimitLabel"
                    v-model="registrationQueueLimitDraft"
                    :disabled="!canEdit"
                    :min="1"
                    :widthInChars="6"
                    @onBlur="onRegistrationQueueLimitBlur">
                    <template #aside>
                        <p v-if="participantStatus && queueSpotsLeft !== registrationQueueLimit" class="align-with-input m-0-b p-xs-l">
                            {{ formatCount($Resources.EventQueueLimitSpotsLeftText, queueSpotsLeft) }}
                        </p>
                    </template>
                </FormInput>
            </div>
        </template>

        <EventEditorDetailsNav v-if="event" :eventId="props.eventId" />
    </div>
</template>
